// ---------- saved progress ----------
// What is stored is the ORDER OF TAPS, not a grid of booleans. One tap fills one cell, so the
// list IS the filled state, in the order it was painted — one piece of data instead of two, and
// it doubles as the script for the replay of a finished puzzle. Three bytes per filled cell:
// a 80x120 puzzle carried to the end costs about 25 kB of indices, 33 kB once base64'd.
// (Back when a tap filled its whole contiguous zone the same puzzle fitted in 2.7 kB. Order
// alone was worth that; painting cell by cell is what made the list as long as the drawing.)
//
// localStorage REMAINS the source of truth, and the cloud is a mirror kept behind it (src/
// sync.ts). Playing must never wait on a network: a fill writes locally, the push happens after
// and may fail without anyone noticing. The device always sends its complete list, so a lost
// push costs nothing but a delay.
//
// The merge between two devices is a set union, and that is the whole reason this file stores
// taps rather than filled cells: a tap landing on an already-filled cell is a no-op on replay,
// so the list is a grow-only set and the picture a monotone function of it. No conflict to
// arbitrate, no version vector, no last-writer-wins losing someone's afternoon.

/**
 * L'ancien emplacement : UN objet portant tous les puzzles. Gardé pour la seule migration —
 * plus rien ne l'écrit.
 */
const KEY_V1 = 'codigrid:progress:v1';
/**
 * Le nouvel emplacement : une clé PAR PUZZLE, et le préfixe est ce qui les rassemble.
 *
 * Le format d'avant coûtait son poids entier à chaque écriture. Sauvegarder une partie, c'est
 * `read()` puis `write()`, donc un JSON.parse et un JSON.stringify de TOUS les puzzles jamais
 * joués — listes de coups et vignettes PNG en base64 comprises — et cela toutes les 800 ms
 * pendant qu'un doigt balaie la feuille. Mesuré : 2 ms à cinq puzzles, 21 ms à cinquante sur un
 * téléphone, soit une image de 60 Hz sautée à chaque sauvegarde. Le coût grandissait avec la
 * bibliothèque du joueur : plus il jouait, plus le jeu s'accrochait.
 *
 * Découpé, une sauvegarde ne sérialise plus que le puzzle en cours. Ce sont les parcours de
 * l'ensemble — le catalogue, la passe de lancement — qui paient l'énumération, et ceux-là
 * arrivent une fois, pas trois fois par seconde.
 */
const PREFIX = 'codigrid:progress:v2:';

const keyOf = (id: string): string => PREFIX + id;
/** Coalescing window. A fill triggers a save, a sweep fills dozens of cells per second and a
 *  500-cell grid holds up to 250 000 of them: serializing the whole list on every one would be
 *  quadratic. */
const WRITE_MS = 800;

interface Record_ {
  fp: string; // target fingerprint, see below
  taps: string; // base64, 3 bytes per cell index
  pct: number; // kept so the catalogue can show progress without loading the target
  done: boolean;
  /** 0 to 5 stars, absent until the player rates. Not grow-only like the rest of this record —
   *  an opinion can change in either direction — so it is set through `rateProgress()` alone,
   *  never through `saveProgress()`'s coalesced writes. */
  rating?: number;
  /** PNG data URL of the sheet as it stands. Cached rather than recomputed from the taps: the
   *  catalogue only receives a puzzle summary, not its target, so it could not redraw it.
   *  Deliberately NOT synced: it is a few dozen kB of pixels reconstructible from the taps, and
   *  it would dwarf the save it illustrates. A device that has never opened a puzzle shows the
   *  question mark until it does. */
  thumb?: string;
  /** ISO. Date de dernière touche, et ce n'est plus une réserve pour un futur merge : c'est
   *  l'ORDRE DU FLUX. Les deux étagères « en cours » et « terminés » se lisent de la plus
   *  récente à la plus ancienne (cf. sort() dans home.ts), donc ce champ est devenu quelque
   *  chose que le joueur VOIT. Deux conséquences : il doit être écrit par tous les chemins
   *  d'écriture (il l'est, quatre), et une progression qui arrive du serveur doit reprendre la
   *  date du SERVEUR et non l'heure de la fusion — sans quoi une réinstallation aplatirait tout
   *  l'historique sur le même instant et l'ordre serait celui du hasard. */
  at: string;
  /** Set when the cloud index reported a save this device doesn't hold in full — a fresh
   *  install, or another device that went further. The percentage is then true but the taps
   *  are not, so opening this puzzle has to wait for the cloud copy instead of replaying a
   *  local list it knows to be short. Cleared by adoptRemote(). */
  stale?: boolean;
}
/** La forme de l'ANCIEN stockage : tous les puzzles dans un seul objet. Ne survit que le temps
 *  de la migration (cf. migrate) — plus rien n'écrit sous cette forme. */
type Store = Record<string, Record_>;

/**
 * Identifies the grid a save belongs to. Editing a puzzle in administration changes its codes:
 * the stored taps would then designate cells whose symbol has moved, and the replay would fill
 * something else entirely — silently. A mismatch discards the save instead.
 */
export function targetFingerprint(target: readonly number[][]): string {
  let h = 0x811c9dc5; // FNV-1a
  const mix = (v: number) => {
    h = Math.imul(h ^ v, 0x01000193) >>> 0;
  };
  mix(target.length);
  mix(target[0]?.length ?? 0);
  for (const row of target) for (const v of row) mix(v);
  return h.toString(36);
}

function encode(taps: readonly number[]): string {
  const b = new Uint8Array(taps.length * 3); // 3 bytes: up to 500x500 = 250 000 cells
  for (let i = 0; i < taps.length; i++) {
    const v = taps[i];
    b[i * 3] = (v >>> 16) & 255;
    b[i * 3 + 1] = (v >>> 8) & 255;
    b[i * 3 + 2] = v & 255;
  }
  let s = '';
  // chunked: spreading 70 000 arguments into fromCharCode blows the argument limit
  for (let i = 0; i < b.length; i += 8192) s += String.fromCharCode(...b.subarray(i, i + 8192));
  return btoa(s);
}

function decode(s: string): number[] {
  const bin = atob(s);
  const out: number[] = [];
  for (let i = 0; i + 2 < bin.length; i += 3)
    out.push((bin.charCodeAt(i) << 16) | (bin.charCodeAt(i + 1) << 8) | bin.charCodeAt(i + 2));
  return out;
}

// Every access below is guarded: a full quota or a browser in private mode must never take the
// game down. Losing a save is a nuisance, a crash mid-puzzle is not.

/**
 * Ce que la migration n'a PAS pu déplacer, gardé en mémoire pour la durée de la session.
 *
 * Le seul cas où cela sert est un quota déjà plein : la migration recopie avant d'effacer, donc
 * elle demande brièvement les deux formats à la fois. Plutôt que de faire disparaître des parties
 * de l'écran jusqu'au prochain lancement, on continue de les lire ici, et l'ancienne clé reste en
 * place pour qu'une session ultérieure retente. Vaut nul dans le cas normal.
 */
let legacy: Record<string, Record_> | null = null;
let migrated = false;

/**
 * Éclate l'ancien objet unique en une clé par puzzle. Tourne une fois, au premier accès.
 *
 * L'ordre est le seul point délicat : on ÉCRIT tout, et on n'efface l'ancienne clé que si tout
 * est passé. L'inverse — effacer d'abord pour faire de la place — perdrait la partie d'un joueur
 * dont le quota déborde en cours de route, et c'est précisément le joueur qui en a le plus.
 */
function migrate(): void {
  if (migrated) return;
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(KEY_V1);
  } catch {
    return; // pas de stockage du tout : rien à migrer, rien à perdre
  }
  // Le cas courant, et il ne pose AUCUN drapeau : une fois la migration faite l'ancienne clé
  // n'existe plus, donc tout ce que coûte ce chemin est une lecture qui rend null. Le drapeau
  // ci-dessous ne sert qu'à ne pas s'acharner sur un échec.
  if (!raw) return;
  let old: Store;
  try {
    old = (JSON.parse(raw) as Store) ?? {};
  } catch {
    // Illisible : il n'y a rien à sauver, et le garder ferait retenter à chaque lancement.
    try {
      localStorage.removeItem(KEY_V1);
    } catch {
      /* ignore */
    }
    return;
  }
  let complete = true;
  for (const [id, rec] of Object.entries(old)) {
    try {
      // Ne jamais écraser ce qui est déjà au nouveau format : c'est forcément le plus récent.
      if (localStorage.getItem(keyOf(id)) === null)
        localStorage.setItem(keyOf(id), JSON.stringify(rec));
    } catch {
      complete = false;
      (legacy ??= {})[id] = rec;
    }
  }
  if (!complete) {
    // L'ancienne clé RESTE, pour qu'une prochaine session retente avec, peut-être, de la place.
    // Le drapeau, lui, évite de recommencer à chaque lecture d'ici là.
    migrated = true;
    return;
  }
  try {
    localStorage.removeItem(KEY_V1);
  } catch {
    /* ignore */
  }
}

function readOne(id: string): Record_ | null {
  migrate();
  try {
    const raw = localStorage.getItem(keyOf(id));
    if (raw) return JSON.parse(raw) as Record_;
  } catch {
    /* illisible : traité comme absent */
  }
  return legacy?.[id] ?? null;
}

/**
 * LE STOCKAGE PLEIN. Le 2026-10-15, une joueuse assidue finissait le quotidien, voyait la carte
 * de fin… et retrouvait au catalogue la feuille à faire, sans vignette, et toutes les suivantes
 * pareil. Chaque feuille finie garde ici sa liste de coups entière — ~130 k caractères pour les
 * 33 000 cases d'un quotidien — plus sa vignette, et le localStorage d'une WebView plafonne à
 * 5,2 M caractères (mesuré sous Chrome 155 : `QuotaExceededError` au-delà). Une trentaine de
 * feuilles finies remplissent tout. Ce `catch` avalait le refus en silence : plus rien ne
 * s'écrivait, sans un mot — ni au joueur, ni dans les journaux.
 *
 * Ce qui se libère sans rien perdre : la liste de coups d'une feuille que le SERVEUR sait finie,
 * sur la même grille. Il en a la liste entière (l'envoi qui l'a déclarée finie la portait, et
 * il fusionne par union), et on garde ici le pourcentage, la fin et la vignette — tout ce que le
 * catalogue montre. La feuille passe `stale` : rouverte, elle va chercher sa liste au serveur
 * (needsPull → pullPuzzle), exactement comme après une réinstallation. On paie un aller-retour
 * à la réouverture d'une feuille finie, pour le rejeu, et c'est tout.
 *
 * Les autres restent intactes : une partie en cours, ou finie mais dont le serveur n'a pas
 * encore accusé la fin (hors ligne), n'existe peut-être qu'ici.
 */
const doneOnServer = new Map<string, string>(); // id → empreinte de la grille finie là-bas

/**
 * Vrai quand makeRoom() a déjà libéré tout ce qu'il pouvait. Un stockage plein SANS rien à
 * libérer (des parties en cours seulement) referait sinon le tour complet — 5 Mo de JSON relus
 * et reparsés — à chaque sauvegarde, soit toutes les 800 ms d'un doigt qui balaie : de quoi
 * faire saccader une tablette d'entrée de gamme. Rien de neuf ne devient libérable tant que le
 * serveur n'annonce pas une nouvelle fin, d'où la remise à faux dans markDoneOnServer().
 */
let roomSpent = false;

function markDoneOnServer(id: string, fp: string): void {
  if (doneOnServer.get(id) === fp) return;
  doneOnServer.set(id, fp);
  roomSpent = false;
}

/** Prévenu quand une écriture échoue MÊME après avoir fait de la place — branché par sync.ts,
 *  ce module ne parlant pas au réseau. Une fois par session : le rapport dit que ça arrive, pas
 *  combien de fois. */
let storageFull: ((detail: { id: string; chars: number; freed: number }) => void) | null = null;
let fullReported = false;

export function setStorageFullReport(f: typeof storageFull): void {
  storageFull = f;
}

function tryWrite(id: string, value: string): boolean {
  try {
    localStorage.setItem(keyOf(id), value);
    if (legacy) delete legacy[id]; // il est au nouveau format désormais
    return true;
  } catch {
    return false; // quota, mode privé
  }
}

/** Retire la liste de coups des feuilles finies côté serveur, sauf `keep` — celle qu'on est en
 *  train d'écrire. Rend le nombre de caractères libérés. Une réécriture PLUS COURTE passe même
 *  sous un quota plein : le navigateur compte la taille après remplacement. */
function makeRoom(keep: string): number {
  let freed = 0;
  for (const id of allIds()) {
    if (id === keep) continue;
    const rec = readOne(id);
    if (!rec || !rec.done || !rec.taps || doneOnServer.get(id) !== rec.fp) continue;
    // Jamais une CRÉATION, même si le serveur en annonce la fin : depuis le 2026-10-30 il efface
    // sa copie en la rendant (cf. settleCreation dans sync.ts), et la liste vidée ici ne se
    // retrouverait nulle part. `c_` est CREATION_PREFIX (src/api.ts), recopié pour que ce module
    // reste sans dépendance.
    if (id.startsWith('c_')) continue;
    if (tryWrite(id, JSON.stringify({ ...rec, taps: '', stale: true }))) freed += rec.taps.length;
  }
  return freed;
}

function writeOne(id: string, rec: Record_): void {
  migrate();
  const value = JSON.stringify(rec);
  if (tryWrite(id, value)) return;
  const freed = roomSpent ? 0 : makeRoom(id);
  roomSpent = true;
  if (freed && tryWrite(id, value)) return;
  // Rien à libérer, ou pas assez : on joue sans sauvegarder, comme avant — mais on le dit.
  if (!fullReported) {
    fullReported = true;
    storageFull?.({ id, chars: value.length, freed });
  }
}

/** Les identifiants de tous les puzzles connus localement. */
function allIds(): string[] {
  migrate();
  const out = new Set<string>();
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX)) out.add(k.slice(PREFIX.length));
    }
  } catch {
    /* pas d'énumération : il reste ce que la migration a gardé sous la main */
  }
  for (const id of Object.keys(legacy ?? {})) out.add(id);
  return [...out];
}

/** Le store entier, reconstitué. Pour les parcours d'ensemble UNIQUEMENT — le catalogue et la
 *  passe de lancement : c'est l'opération que le découpage rend coûteuse, et la seule. */
function readAll(): Array<[string, Record_]> {
  const out: Array<[string, Record_]> = [];
  for (const id of allIds()) {
    const rec = readOne(id);
    if (rec) out.push([id, rec]);
  }
  return out;
}

export interface Saved {
  taps: number[];
  done: boolean;
  rating?: number;
}

export function loadProgress(id: string, fp: string): Saved | null {
  const rec = readOne(id);
  if (!rec || rec.fp !== fp) return null;
  try {
    return { taps: decode(rec.taps), done: rec.done, rating: rec.rating };
  } catch {
    return null; // corrupt payload: start over rather than replay nonsense
  }
}

/** Les taps sont gardés BRUTS, pas encodés : voir saveProgress. */
let pending: {
  id: string;
  rec: Omit<Record_, 'taps'>;
  taps: readonly number[];
} | null = null;
let timer = 0;

/**
 * Le sujet de la prochaine photo : la feuille en cours, et de quoi la rasteriser.
 *
 * Retenu ICI et non dans `pending`, et c'est ce qui rend la prise au départ possible. Au moment
 * où le joueur quitte la feuille, `pending` est le plus souvent VIDE — la fenêtre de coalescence
 * a déjà écrit le dernier coup une demi-seconde plus tôt — donc un vidage qui sort tout de suite
 * n'aurait rien sous la main à photographier. Rangé à part, le départ trouve son sujet dans tous
 * les cas.
 *
 * Remis à null par la prise : sortir deux fois sans qu'une seule case ait été peinte entre les
 * deux ne repaie pas la rastérisation.
 */
let subject: { id: string; shoot: () => string | null } | null = null;

/**
 * Les feuilles disparues pendant la session — une création supprimée, ici ou sur un autre
 * téléphone (cf. forgetProgress). Leur identifiant ne resservira jamais : on n'écrit plus rien pour
 * elles, même si le joueur continue à peindre celle qui est ouverte. En mémoire seulement : au
 * prochain lancement, la feuille n'est plus dans la bibliothèque et ne peut plus être ouverte.
 */
const gone = new Set<string>();

/**
 * La fenêtre de coalescence arrive à échéance : on écrit la progression, et RIEN DE PLUS.
 *
 * **La vignette ne se prend pas ici**, et c'est tout l'intérêt de la fonction — voir
 * leaveSheet(), qui est la seule à la prendre et qui explique ce que ça coûtait. `shot` n'est
 * renseigné que par elle : quiconque appellerait ce vidage-ci pour une SORTIE de feuille
 * écrirait une progression juste sous une vignette périmée.
 */
export function flushProgress(shot?: string | null): void {
  if (timer) {
    clearTimeout(timer);
    timer = 0;
  }
  if (!pending) return;
  const { id, rec } = pending;
  // L'encodage se fait ICI, une fois par écriture — et non à chaque case peinte. Voir saveProgress.
  const taps = encode(pending.taps);
  const prev = readOne(id);
  pending = null;
  // Keep the previous image rather than dropping it: a flush with nothing drawn yet must not
  // erase a thumbnail already earned.
  writeOne(id, { ...rec, taps, thumb: shot ?? prev?.thumb });
  // Local first, network after, and never awaited: the cloud is a mirror, not a dependency.
  sync?.(id, { fp: rec.fp, taps, pct: rec.pct, done: rec.done });
}

/**
 * Le joueur quitte la feuille : on écrit ce qui reste en attente, ET on prend la vignette.
 *
 * Elle se prenait auparavant à CHAQUE vidage, c'est-à-dire toutes les 800 ms tant qu'un doigt
 * peignait. Mesuré sur une feuille émulée en 412 × 900 : renderThumb() parcourt TOUTE la grille
 * — sans cellWindow(), c'est le seul coût du jeu qui grandit avec la feuille et jamais avec
 * l'écran — puis encode un PNG. 8,4 ms sur une grille de 72 de côté, 50 ms à 200, 80 ms à 250,
 * 103 ms à 300, pour 73 Ko de base64. Un dixième de seconde de processeur toutes les huit
 * dixièmes, planté au milieu d'une boucle de rendu à 60 images par seconde : de la batterie
 * brûlée et des images sautées, pour une image que PERSONNE ne regarde pendant la partie — elle
 * n'illustre que la carte du catalogue.
 *
 * Elle se prend donc là où elle sert : à la porte. Trois sorties, et ce sont les seules qu'une
 * feuille ait — le retour au catalogue (openHome), le passage en arrière-plan et la fermeture
 * (wireProgressFlush), et la dernière case posée, qui ne laisse plus rien à peindre
 * (saveProgress, sur `done`). Sur un téléphone, une partie ne se termine pas autrement.
 *
 * L'ordre compte : la photo est prise AVANT le vidage, pour qu'elle voyage avec l'écriture des
 * coups au lieu de coûter une seconde écriture du dossier.
 */
export function leaveSheet(): void {
  const shot = subject?.shoot() ?? null;
  const id = subject?.id ?? null;
  subject = null;
  if (pending) {
    flushProgress(shot);
    return;
  }
  // Rien en attente : le cas ORDINAIRE d'une sortie, la fenêtre ayant déjà écrit le dernier
  // coup. La vignette rejoint alors le dossier tel qu'il est déjà sur le disque.
  if (!shot || !id) return;
  const rec = readOne(id);
  // Pas de dossier : une feuille ouverte et refermée sans qu'une case ait été peinte. Il n'y a
  // rien à illustrer, et écrire un dossier pour la seule vignette inventerait une partie.
  if (rec) writeOne(id, { ...rec, thumb: shot });
}

/**
 * `thumb` is a thunk, not a value: it is only ever called when the player LEAVES the sheet
 * (leaveSheet), never on the coalesced writes a sweep produces three times a second.
 */
export function saveProgress(
  id: string,
  fp: string,
  taps: readonly number[],
  pct: number,
  done: boolean,
  thumb?: () => string | null,
): void {
  if (gone.has(id)) return;
  pending = {
    id,
    rec: { fp, pct, done, at: new Date().toISOString() },
    // Une COPIE, et c'est ce qui rend le report possible : `taps` est le tableau vivant de la
    // partie (S.taps), qu'un resetPlay() peut vider avant que le minuteur ne se déclenche —
    // garder la référence ferait écrire une progression vide par-dessus la vraie.
    taps: taps.slice(),
  };
  subject = thumb ? { id, shoot: thumb } : null;
  // Finishing is written through at once: it is the state worth never losing. Et par la porte,
  // vignette comprise : la feuille finie est LA vignette que la carte portera pour toujours, et
  // il ne reste plus une case à peindre qui pourrait la faire changer. Attendre la sortie la
  // mettrait à la merci d'un processus tué entre les deux.
  if (done) {
    leaveSheet();
    return;
  }
  // Bare setTimeout, not window.setTimeout: the module has to be loadable outside a browser
  // (tests), and `window` alone would throw on import-time-free code paths.
  //
  // Enveloppé et non passé nu : `flushProgress` prend désormais un premier paramètre, et une
  // implémentation de setTimeout qui rappelle avec un argument (le retard, chez certains
  // portages) le lui livrerait comme une vignette.
  if (!timer) timer = setTimeout(() => flushProgress(), WRITE_MS) as unknown as number;
}

/**
 * Écrit la vignette d'une feuille qu'on n'est PAS en train de quitter.
 *
 * `flushProgress`/`leaveSheet` ne savent la prendre que de la feuille ouverte, sur le canvas
 * vivant. Le calendrier du puzzle du jour a le cas inverse : un jour peint sur un AUTRE appareil
 * a bien sa progression (les taps sont synchronisés) mais pas son image (la vignette est locale
 * et ne voyage pas), et il la reconstruit hors écran — cf. rebuildThumbs() dans src/daily.ts.
 *
 * `fp` est exigée et vérifiée : une vignette rasterisée à partir d'une grille qui a changé depuis
 * ne montrerait pas ce que le joueur a peint. Sans ligne locale, ou avec une empreinte qui ne
 * correspond pas, on n'écrit rien — il n'y a rien à illustrer.
 */
export function setThumb(id: string, fp: string, thumb: string): void {
  const rec = readOne(id);
  if (!rec || rec.fp !== fp) return;
  writeOne(id, { ...rec, thumb });
}

export interface CardProgress {
  pct: number;
  done: boolean;
  thumb?: string;
  /** ISO, la date de dernière touche — l'ordre des deux étagères du flux. */
  at: string;
}

/** Progress of every puzzle, for the catalogue. */
export function progressIndex(): Map<string, CardProgress> {
  const out = new Map<string, CardProgress>();
  for (const [id, rec] of readAll())
    out.set(id, { pct: rec.pct, done: rec.done, thumb: rec.thumb, at: rec.at });
  return out;
}

// ---------- cloud mirror ----------
// This module knows nothing about the network: src/sync.ts registers a callback and does the
// talking. Same shape as the rest of the codebase (wireAnim, wireCounter) — the dependency
// points one way, and progress.ts stays testable without a backend.

/** What travels: the identical base64 blob, so nothing is transcoded end to end. */
export interface Snapshot {
  fp: string;
  taps: string;
  pct: number;
  done: boolean;
  rating?: number;
}

let sync: ((id: string, snap: Snapshot) => void) | null = null;
let syncReset: ((id: string) => void) | null = null;

export function setProgressSync(
  push: (id: string, snap: Snapshot) => void,
  reset: (id: string) => void,
): void {
  sync = push;
  syncReset = reset;
}

/**
 * The player's opinion of a puzzle they have already played, set from the star row next to
 * "Revoir l'animation" / "Partager" — never before a save exists, since rating something never
 * opened makes no sense. Bypasses `pending`/`flushProgress`'s 800 ms coalescing on purpose: a
 * star tap is a single discrete action, not a stream of fills to batch, and by the time it is
 * possible at all the puzzle is already `done`, which is the same fast path `saveProgress()`
 * takes to flush at once instead of waiting.
 */
export function rateProgress(id: string, rating: number): void {
  const rec = readOne(id);
  if (!rec) return; // nothing played here yet
  rec.rating = rating;
  writeOne(id, rec);
  sync?.(id, { fp: rec.fp, taps: rec.taps, pct: rec.pct, done: rec.done, rating });
}

/**
 * Starting over. Not a save at pct 0: every other write only ever adds taps, which is what lets
 * two devices merge by union, and a zero pushed into that union means nothing at all — the
 * cloud would hand the old list straight back on the next launch. The record is emptied here
 * and deleted there.
 *
 * The thumbnail goes too: a card showing a half-drawn sheet over a puzzle back at 0% would be
 * lying about where the player stands.
 */
export function resetProgress(id: string, fp: string): void {
  // Any pending write belongs to the game that just ended.
  if (timer) {
    clearTimeout(timer);
    timer = 0;
  }
  pending = null;
  // Le sujet part avec : il photographierait la feuille qu'on vient de jeter, et la prochaine
  // sortie reposerait cette vignette sur un dossier remis à zéro.
  subject = null;
  // La fin que le serveur connaissait est celle de la partie qu'on jette — et syncReset l'efface
  // là-bas. La garder ferait croire à makeRoom() que la PROCHAINE fin de cette feuille est déjà
  // au serveur : finie hors ligne sur un stockage plein, elle serait allégée alors que sa liste
  // n'existe qu'ici.
  doneOnServer.delete(id);
  writeOne(id, { fp, taps: '', pct: 0, done: false, at: new Date().toISOString() });
  syncReset?.(id);
}

/**
 * Une feuille qui n'existe plus — une création que le joueur vient de supprimer (cf. withDelete
 * dans home.ts) : sa sauvegarde locale part avec elle, la clé RETIRÉE comme dans
 * clearAllProgress() juste en dessous. Laissée là, la passe de lancement (sync.ts) la repousserait
 * au serveur, qui garderait la liste de cases d'une feuille disparue. Le serveur a déjà effacé la
 * sienne : DELETE /me/creations emporte la sauvegarde dans la même transaction.
 */
export function forgetProgress(id: string): void {
  // La feuille n'existe plus : plus rien ne s'écrit pour elle de la session (cf. gone), et ce qui
  // attendait d'être écrit part avec elle. Sans ça, l'écriture en attente — ou la suivante, si le
  // joueur peignait encore la feuille ouverte — remettait le dossier en place et relançait un
  // envoi, refusé à son tour : une boucle à chaque fenêtre d'envoi (revue du 2026-10-17).
  gone.add(id);
  if (pending?.id === id) {
    if (timer) {
      clearTimeout(timer);
      timer = 0;
    }
    pending = null;
  }
  if (subject?.id === id) subject = null;
  doneOnServer.delete(id);
  try {
    localStorage.removeItem(keyOf(id));
  } catch {
    /* rien de stocké, rien à retirer */
  }
}

/**
 * Everything, gone — the local half of "delete my data" (cf. src/prefs.ts). The pending write is
 * dropped first and not flushed: it belongs to the very data being erased, and flushing it would
 * write a record back a millisecond after the store was cleared.
 *
 * Les clés sont RETIRÉES et non vidées : une clé vide est un puzzle connu à 0 %, une clé absente
 * est un appareil qui n'y a jamais joué, et c'est cela que le joueur demande à redevenir.
 *
 * Les identifiants sont relevés avant d'effacer quoi que ce soit : `localStorage.key(i)` marche
 * par indice, et supprimer pendant qu'on parcourt décale ce qui reste — un élément sur deux
 * survivrait à l'effacement.
 */
export function clearAllProgress(): void {
  if (timer) {
    clearTimeout(timer);
    timer = 0;
  }
  pending = null;
  // Idem : la sortie qui suit « tout effacer » ne doit pas réécrire une vignette du néant.
  subject = null;
  // Ce que le serveur savait fini appartient au compte qu'on efface : un compte neuf repart de
  // rien, et son premier stockage plein mérite son rapport.
  doneOnServer.clear();
  roomSpent = false;
  fullReported = false;
  const ids = allIds();
  legacy = null;
  try {
    for (const id of ids) localStorage.removeItem(keyOf(id));
    // L'ancienne clé aussi : elle est encore là si une migration n'a pas pu aller au bout, et
    // « tout effacer » ne doit rien laisser derrière qu'un prochain lancement ferait revenir.
    localStorage.removeItem(KEY_V1);
  } catch {
    /* nothing stored, nothing to clear */
  }
}

/** Every local save, for the reconciliation pass at launch — pushes that never made it out
 *  (app killed, plane mode) are found here rather than lost. */
export function localSnapshots(): Array<{ id: string; snap: Snapshot; stale: boolean }> {
  return readAll().map(([id, rec]) => ({
    id,
    snap: { fp: rec.fp, taps: rec.taps, pct: rec.pct, done: rec.done },
    stale: !!rec.stale,
  }));
}

/** True when opening this puzzle should wait for the cloud copy: nothing stored, or a record
 *  the index flagged as behind. Anything else opens instantly from local. */
export function needsPull(id: string): boolean {
  const rec = readOne(id);
  return !rec || !!rec.stale;
}

/**
 * Folds the cloud index into the local store. Only the percentage and the done flag can be
 * learned here — the tap lists aren't in the index — so a record that gains ground is marked
 * `stale` instead of being trusted to replay.
 *
 * A fingerprint mismatch replaces the record outright: the local taps were recorded against a
 * grid that has since been edited, so loadProgress would discard them anyway.
 */
export function mergeRemoteIndex(
  list: ReadonlyArray<{
    puzzleId: string;
    fp: string;
    pct: number;
    done: boolean;
    updatedAt?: string;
  }>,
): boolean {
  // TOUTES les fins connues du serveur d'abord, avant la moindre écriture : c'est précisément en
  // rapatriant ces fins qu'un stockage plein demande de la place (cf. makeRoom), et il doit
  // alors pouvoir puiser dans toute la liste, pas seulement dans ce que la boucle a déjà vu.
  for (const r of list) if (r.done) markDoneOnServer(r.puzzleId, r.fp);
  let changed = false;
  for (const r of list) {
    const local = readOne(r.puzzleId);
    // Only a record of the SAME grid has anything worth keeping.
    const keep = local && local.fp === r.fp ? local : null;
    if (keep && keep.pct >= r.pct && (keep.done || !r.done)) continue;
    writeOne(r.puzzleId, {
      fp: r.fp,
      taps: keep?.taps ?? '',
      pct: Math.max(r.pct, keep?.pct ?? 0),
      done: r.done || !!keep?.done,
      thumb: keep?.thumb,
      // La date du SERVEUR, et l'heure de la fusion seulement à défaut : `at` ordonne le flux
      // (cf. sa déclaration), et une réinstallation qui rapatrie trente sauvegardes les
      // horodaterait toutes à la même seconde — l'ordre du flux serait alors celui dans lequel
      // la boucle est passée, c'est-à-dire aucun.
      at: r.updatedAt ?? new Date().toISOString(),
      stale: true,
    });
    changed = true;
  }
  return changed;
}

/**
 * Writes back the server's merge of this save. The thumbnail stays local — it is never sent
 * and never received.
 *
 * Takes its own shape rather than `Snapshot`: the server's `rating` is `number | null` (SQL
 * NULL), while `Snapshot.rating` is the OUTGOING shape and stays plain `number | undefined` —
 * this module never constructs or sends an explicit `null`. The two only meet here.
 */
export function adoptRemote(
  id: string,
  remote: {
    fp: string;
    taps: string;
    pct: number;
    done: boolean;
    rating?: number | null;
    updatedAt?: string;
  },
): void {
  const prev = readOne(id);
  // Le serveur vient de répondre avec sa fusion : si elle est finie, sa liste est complète là-bas.
  if (remote.done) markDoneOnServer(id, remote.fp);
  // Field by field, not a spread: the server's payload carries puzzleId too, and it has no
  // business ending up in the local record. `updatedAt`, lui, a désormais sa place — il devient
  // `at`, qui ordonne le flux.
  writeOne(id, {
    fp: remote.fp,
    taps: remote.taps,
    pct: remote.pct,
    done: remote.done,
    rating: remote.rating ?? undefined,
    thumb: prev?.thumb,
    at: remote.updatedAt ?? new Date().toISOString(),
    stale: false,
  });
}

/**
 * The pending write would otherwise be lost when the app is backgrounded or killed — the most
 * common way to leave a game on a phone.
 *
 * Par leaveSheet() et non par flushProgress() : passer en arrière-plan EST une sortie de feuille,
 * et c'est même la plus fréquente des trois — c'est là que la vignette du catalogue se prend
 * (voir leaveSheet). Les écouteurs sont enveloppés parce qu'ils passent l'événement en premier
 * argument, et leaveSheet n'en attend aucun.
 */
export function wireProgressFlush(): void {
  addEventListener('pagehide', () => leaveSheet());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') leaveSheet();
  });
}
